import React from 'react';
import GiftIcon from '../GiftIcon';
import { LangContext } from '../../contexts/LangContext';
import { useVerifyServer } from '../../hooks/useVerifyServer';
import './PackCard.css';


function useCountdown(until) {
  const calc = () => {
    if (!until) return null;
    const normalized = /Z|[+-]\d{2}:\d{2}$/.test(until) ? until : until + 'Z';
    const diff = new Date(normalized) - Date.now();
    if (diff <= 0) return null;
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { days, hours, minutes, seconds };
  };

  const [remaining, setRemaining] = React.useState(calc);

  React.useEffect(() => {
    if (!until) return;
    const id = setInterval(() => setRemaining(calc()), 1000);
    return () => clearInterval(id);
  }, [until]);

  return remaining;
}

const ROMAN = ['I', 'II', 'III', 'IV', 'V'];


const LOKI_COIN_ICON  = '5193.gif';
const EVENT_COIN_ICON = '5194.gif';

function LokiGiftRow({ gift, server }) {
  const { t } = React.useContext(LangContext);
  const pc = t.component.packCard;
  const serverMap = useVerifyServer(server);
  const itemId = gift?.icon?.replace(/\.[^.]+$/, '') ?? '';
  const localSrc = itemId ? `img/loki/${itemId}.png` : '';
  const serverSrc = itemId ? `https://wydmisc.raidhut.com.br/icons/${serverMap}/${gift.icon}` : '';
  const [src, setSrc] = React.useState(localSrc);

  if (!gift || gift.icon === 'npextra.png') return null;

  const label = gift.icon === LOKI_COIN_ICON
    ? `${pc.bonusContent}:`
    : gift.icon === EVENT_COIN_ICON
      ? `${pc.packContent}:`
      : null;

  return (
    <div className="loki-gift-row">
      <img
        src={src}
        alt=""
        className="loki-gift-coin"
        onError={() => setSrc(serverSrc)}
      />
      <div className="loki-gift-info">
        {label && <span className="loki-gift-label">{label}</span>}
        <span className="loki-gift-name">{gift.name}</span>
      </div>
      {gift.tooltip && <span className="loki-gift-tooltip">{gift.tooltip}</span>}
    </div>
  );
}

export default function PackCard({ item, server, onAddToCart, cartQty = 0, onQtyChange, onRemove, isElegibleForGift = true, promotionLink = null, giftLink = null, packIndex = null, variant = 'default' }) {
  const { lang, t } = React.useContext(LangContext);
  const pc = t.component.packCard;
  const si = t.page.store.item;

  const locale = lang === 'USD' ? 'en-US' : 'pt-BR';
  const currency = lang === 'USD' ? 'USD' : 'BRL';
  const formattedPrice = (item.brlPriceValue ?? 0).toLocaleString(locale, { style: 'currency', currency });

  const [hovered, setHovered]   = React.useState(false);
  const [openTooltip, setOpenTooltip] = React.useState(null);
  const [flash, setFlash]       = React.useState(false);
  const [btnPop, setBtnPop]     = React.useState(false);
  const cardRef                 = React.useRef(null);
  const countdown = useCountdown(item.availableuntil);

  React.useEffect(() => {
    if (!openTooltip) return;
    const close = (e) => {
      if (!e.target.closest('.info-bubble-wrapper')) setOpenTooltip(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [openTooltip]);

  const chestOffsetY = item.chestOffsetY ?? '0px';
  const bonusOffsetY = item.bonusOffsetY ?? '0px';
  const scale = hovered ? 'scale(1.1)' : 'scale(1)';
  const lokiBg = variant === 'loki' ? 'img/loki/loki.png' : 'img/background-pack-featured--fogo.png';
  const backgroundStyle = hovered
    ? `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('${lokiBg}') center center / cover no-repeat`
    : undefined;
  const chestImgStyle = { transform: `translateY(${chestOffsetY}) ${scale}`, transition: 'transform 0.2s ease' };
  const bonusImgStyle = { transform: `translateY(${bonusOffsetY}) ${scale}`, transition: 'transform 0.2s ease' };

  const visibleGifts = (item.gifts || []).filter(g => g.icon && g.icon !== 'npextra.png');

  const triggerAdd = () => {
    setFlash(true);
    setBtnPop(true);
    setTimeout(() => setFlash(false), 600);
    setTimeout(() => setBtnPop(false), 400);
  };

  const handleCardClick = () => {
    if (window.innerWidth <= 767) return;
    if (cartQty === 0) {
      triggerAdd();
      onAddToCart && onAddToCart(item);
    } else {
      triggerAdd();
      onQtyChange && onQtyChange(item.sku, Math.min(cartQty + 1, 120));
    }
  };

  return (
    <div
      ref={cardRef}
      className={`pack-card${flash ? ' pack-card--added' : ''}${!isElegibleForGift ? ' pack-card--no-gift' : ''}${variant === 'loki' ? ' pack-card--loki' : ''}`}
      style={backgroundStyle ? { background: backgroundStyle } : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleCardClick}
    >
      {/* Header: tag + nome */}
      <div className="card-header">
        <span className="pacote-badge">
          {pc.badge}{packIndex !== null ? ` ${ROMAN[packIndex] ?? packIndex + 1}` : ''}
        </span>
        {variant !== 'loki' && <span className="card-name">{item.name}</span>}
      </div>

      {variant === 'loki' && item.status === 'pre' && (
        <div className="loki-prevenda-header">
          <img src="img/arrow-left.png" alt="" className="loki-wing" />
          <span className="loki-prevenda-text">{pc.preSale}</span>
          <img src="img/arrow-right.png" alt="" className="loki-wing" />
        </div>
      )}

      {variant === 'loki' ? (
        <div className="loki-items-area">
          <div className="loki-gifts-list">
            {[...visibleGifts].reverse().map((gift, i) => (
              <LokiGiftRow key={i} gift={gift} server={server} />
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Imagem do baú + item bônus */}
          <div className="chest-area">
            <img
              src={item.image}
              alt={item.name}
              className="chest-image"
              style={{ ...item.chestImageSize, ...chestImgStyle, marginRight: item.chestMarginRight ?? '0px' }}
            />
            {item.bonusImage && (
              <img
                src={item.bonusImage}
                alt="bonus"
                className="bonus-image"
                style={{ ...item.bonusImageSize, ...bonusImgStyle }}
              />
            )}
          </div>

          {/* Valor em NP com setas */}
          <div className="np-row">
            <img src="img/arrow-left.png" alt="" className="np-arrow" />
            <span className="np-value">{item.npPrice}</span>
            <img src="img/icons/NPs.svg" alt="NP" className="np-icon" />
            <img src="img/arrow-right.png" alt="" className="np-arrow" />
          </div>

          {/* Divider */}
          <img src="img/divider.svg" alt="" className="divider" />

          {/* Conteúdo Bônus */}
          <div className="bonus-area">
            <div className="bonus-label">
              {pc.bonusContent}
              <div
                className="info-bubble-wrapper"
                onClick={e => { e.stopPropagation(); setOpenTooltip(openTooltip === 'bonus' ? null : 'bonus'); }}
              >
                <span className="info-bubble">?</span>
                <div
                  className={`info-tooltip info-tooltip--right${openTooltip === 'bonus' ? ' info-tooltip--open' : ''}`}
                >
                  {pc.bonusContentTooltip}
                  <a
                    href={promotionLink || "https://wydglobal.raidhut.com/"}
                    target="_blank"
                    rel="noreferrer"
                    className="info-tooltip-link"
                    onMouseDown={e => e.stopPropagation()}
                  >
                    {pc.bonusContentTooltipLink}
                  </a>
                </div>
              </div>
            </div>
            {visibleGifts.length > 0 ? (
              <div className="bonus-icons">
                {visibleGifts.map((gift, i) => (
                  <GiftIcon key={i} gift={gift} server={server} />
                ))}
              </div>
            ) : (
              <div className="bonus-icons">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bonus-placeholder" />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Presente Garantido */}
      {variant !== 'loki' && isElegibleForGift && (
        <div className="presente-text">
          <span className="presente-label">
            {(() => {
              const text = pc.guaranteedGift;
              const lastSpace = text.lastIndexOf(' ');
              if (lastSpace === -1) return text;
              return <><span className="presente-first-line">{text.slice(0, lastSpace)}</span>{' '}{text.slice(lastSpace + 1)}</>;
            })()}
          </span>
          <div className="info-bubble-wrapper" onClick={e => { e.stopPropagation(); setOpenTooltip(openTooltip === 'gift' ? null : 'gift'); }}>
            <span className="info-bubble">?</span>
            <div className={`info-tooltip info-tooltip--right${openTooltip === 'gift' ? ' info-tooltip--open' : ''}`}>
              {pc.guaranteedGiftTooltip}
              <a
                href={giftLink || "https://wydglobal.raidhut.com/"}
                target="_blank"
                rel="noreferrer"
                className="info-tooltip-link"
                onMouseDown={e => e.stopPropagation()}
              >
                {pc.bonusContentTooltipLink}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Preço BRL + botão */}
      <div className="price-area">
        <div className="price-row">
          <span className="brl-price">{formattedPrice}</span>
          <div className="btn-col">
            {cartQty > 0 ? (
              <div className="add-to-cart-btn cart-qty-controls" onClick={e => e.stopPropagation()}>
                <button
                  className="cart-ctrl-btn"
                  onClick={() => cartQty === 1 ? onRemove(item.sku) : onQtyChange(item.sku, cartQty - 1)}
                >
                  {cartQty === 1
                    ? <img src="img/icons/thrash-black.svg" alt="remover" className="cart-ctrl-trash" />
                    : '−'}
                </button>
                <span className="cart-ctrl-qty">{cartQty}</span>
                <button
                  className="cart-ctrl-btn"
                  onClick={() => onQtyChange(item.sku, Math.min(cartQty + 1, 120))}
                >+</button>
              </div>
            ) : (
              <button
                className={`add-to-cart-btn${btnPop ? ' add-to-cart-btn--pop' : ''}`}
                onClick={e => { e.stopPropagation(); triggerAdd(); onAddToCart && onAddToCart(item); }}
              >
                {pc.addToCart}
                <img src="img/cart.png" alt="carrinho" className="cart-icon" />
              </button>
            )}
            {countdown ? (
              <div className={`countdown-display${countdown.days === 0 && countdown.hours < 2 ? ' countdown-display--urgent' : ''}`}>
                {countdown.days > 0 && (
                  <div className="countdown-block">
                    <span className="countdown-value">{countdown.days}</span>
                    <span className="countdown-label">{si.days}</span>
                  </div>
                )}
                <div className="countdown-block">
                  <span className="countdown-value">{String(countdown.hours).padStart(2, '0')}</span>
                  <span className="countdown-label">{si.hours}</span>
                </div>
                <div className="countdown-sep">:</div>
                <div className="countdown-block">
                  <span className="countdown-value">{String(countdown.minutes).padStart(2, '0')}</span>
                  <span className="countdown-label">{si.minutes}</span>
                </div>
                <div className="countdown-sep">:</div>
                <div className="countdown-block">
                  <span className="countdown-value">{String(countdown.seconds).padStart(2, '0')}</span>
                  <span className="countdown-label">{si.seconds}</span>
                </div>
              </div>
            ) : item.limitText ? (
              <span className="limit-text">{item.limitText}</span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
